import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Clock, ClockClockwise, Power, PlayCircle, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { api, euro, formatApiError } from "../lib/api";
import MoneyInput from "./MoneyInput";

function timeAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const hh = Math.floor(ms / 3_600_000);
    const mm = Math.floor((ms % 3_600_000) / 60_000);
    if (hh === 0) return `${mm}m`;
    return `${hh}h${String(mm).padStart(2, "0")}`;
}

export function ShiftWidget() {
    const { user, agencyId, currentShift, refreshShift, setUser } = useAuth();
    const [openModal, setOpenModal] = useState(null); // 'open' | 'close' | null
    const [, forceTick] = useState(0);
    const navigate = useNavigate();
    // ref al refreshShift più recente: evita la closure stale (agencyId=null al
    // primo mount dopo il login) che azzerava currentShift al visibilitychange.
    const refreshShiftRef = useRef(refreshShift);
    refreshShiftRef.current = refreshShift;

    // Il timer "Turno · 1h23" è calcolato da Date.now() - opened_at ad ogni
    // render, quindi non va MAI fuori sincrono (il calcolo delle ore pagate si
    // basa sul calendario turni, non su questo display). Però senza un ticker
    // il componente non si ri-renderizza da solo: su mobile, se il telefono va
    // in stop/il browser va in background, il numero resta fermo finché l'utente
    // non naviga altrove. Qui forziamo un refresh periodico + un refresh
    // immediato al ritorno in primo piano (visibilitychange/focus/pageshow),
    // così il contatore riprende subito a girare con il valore corretto.
    useEffect(() => {
        const tick = () => forceTick((t) => t + 1);
        const onVisible = () => {
            if (document.visibilityState === "visible") {
                tick();
                refreshShiftRef.current(); // riallinea lo stato turno col server dopo stop/sleep
            }
        };
        const interval = setInterval(tick, 30_000);
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", tick);
        window.addEventListener("pageshow", tick);
        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", tick);
            window.removeEventListener("pageshow", tick);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!user || !agencyId) return null;

    const hasOpen = !!currentShift;
    const isOperator = user.role === "operator";

    return (
        <>
            {hasOpen ? (
                <button
                    onClick={() => setOpenModal("close")}
                    className="flex items-center gap-2 px-2 md:px-3 py-2 border border-emerald-500 text-emerald-700 hover:bg-emerald-50 transition-colors"
                    data-testid="shift-close-btn"
                    title={`Turno aperto da ${timeAgo(currentShift.opened_at)} · click per chiudere`}
                >
                    <ClockClockwise size={16} weight="bold" />
                    {/* mobile: short timer; 2xl+: "Turno · 1h23" */}
                    <span className="text-xs font-bold whitespace-nowrap md:hidden">
                        {timeAgo(currentShift.opened_at)}
                    </span>
                    <span className="text-xs font-bold whitespace-nowrap hidden 2xl:inline">
                        Turno · {timeAgo(currentShift.opened_at)}
                    </span>
                </button>
            ) : (
                <button
                    onClick={() => setOpenModal("open")}
                    className={`flex items-center gap-2 px-2 md:px-3 py-2 border transition-colors ${
                        isOperator
                            ? "border-signal text-signal hover:bg-signal hover:text-white animate-pulse"
                            : "border-zinc-300 text-zinc-600 hover:border-ink hover:text-ink"
                    }`}
                    data-testid="shift-open-btn"
                    title={isOperator ? "Devi aprire un turno per operare" : "Apri turno"}
                >
                    <PlayCircle size={16} weight="bold" />
                    {/* mobile: "turno"; md-xl: icon only; 2xl+: "Apri turno" */}
                    <span className="text-xs font-bold whitespace-nowrap md:hidden">turno</span>
                    <span className="text-xs font-bold whitespace-nowrap hidden 2xl:inline">
                        Apri turno
                    </span>
                </button>
            )}

            {openModal === "open" && (
                <OpenShiftModal
                    agencyId={agencyId}
                    onClose={() => setOpenModal(null)}
                    onSuccess={async () => {
                        await refreshShift();
                        setOpenModal(null);
                    }}
                />
            )}
            {openModal === "close" && currentShift && (
                <CloseShiftModal
                    shift={currentShift}
                    agencyId={agencyId}
                    onClose={() => setOpenModal(null)}
                    onSuccess={() => {
                        // Forced logout: cookie già rimosso server-side. Clear local state.
                        localStorage.removeItem("token");
                        localStorage.removeItem("agency_id");
                        setUser(false);
                        toast.success("Turno chiuso. Esegui di nuovo il login per continuare.");
                        navigate("/login");
                    }}
                />
            )}
        </>
    );
}


function detectDeviceType() {
    if (typeof navigator === "undefined") return "desktop";
    const ua = navigator.userAgent || "";
    // Solo smartphone "veri": niente touch-screen detection, perché molti PC
    // aziendali/all-in-one hanno uno schermo touch e verrebbero scambiati per
    // telefoni, finendo comunque bloccati dal controllo posizione nonostante
    // la scelta "niente blocco da PC". I tablet (Android senza "Mobile", iPad)
    // sono volutamente considerati "desktop": in caso di dubbio, meglio NON
    // bloccare che bloccare un PC aziendale per errore.
    const isPhoneUA = /iPhone|iPod|Android.*Mobile|Windows Phone|BlackBerry/i.test(ua);
    return isPhoneUA ? "mobile" : "desktop";
}

function OpenShiftModal({ agencyId, onClose, onSuccess }) {
    const [baseline, setBaseline] = useState(null);
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get(`/wallets?agency_id=${agencyId}`).then((r) => {
            const total = r.data
                .filter((w) => ["cassa", "cassaforte"].includes(w.type))
                .reduce((s, w) => s + (w.balance || 0), 0);
            setBaseline(total);
        });
    }, [agencyId]);

    const getPosition = () =>
        new Promise((resolve) => {
            if (!navigator.geolocation) {
                resolve({ lat: null, lng: null, accuracy: null });
                return;
            }
            navigator.geolocation.getCurrentPosition(
                (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
                () => resolve({ lat: null, lng: null, accuracy: null }),
                { enableHighAccuracy: true, timeout: 8000 },
            );
        });

    const submit = async () => {
        setSaving(true);
        try {
            const device_type = detectDeviceType();
            // Il controllo posizione si applica solo da smartphone/tablet: da PC
            // fisso la geolocalizzazione di rete non è abbastanza affidabile, quindi
            // evitiamo anche di chiedere il permesso inutilmente.
            const { lat, lng, accuracy } =
                device_type === "mobile" ? await getPosition() : { lat: null, lng: null, accuracy: null };
            const { data } = await api.post("/shifts/open", {
                agency_id: agencyId,
                notes,
                lat,
                lng,
                accuracy,
                device_type,
            });
            if (data.is_late) {
                toast.warning(`Turno aperto con ${data.late_minutes} min di ritardo rispetto alle ${data.scheduled_start_time}`);
            } else {
                toast.success("Turno aperto");
            }
            onSuccess();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return createPortal((
        <div
            className="fixed inset-0 bg-white/80 backdrop-blur-sm z-[60] overflow-y-auto"
            onClick={onClose}
            data-testid="shift-open-modal"
        >
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="w-full max-w-md bg-white border border-zinc-200 p-6 md:p-8 shadow-2xl"
                >
                <div className="overline mb-3 flex items-center gap-2">
                    <PlayCircle size={12} weight="bold" /> Apri turno
                </div>
                <h2 className="text-3xl font-black tracking-tighter mb-3">
                    Inizio turno
                </h2>
                <div className="text-sm text-zinc-600 mb-6">
                    Verrà fotografata la cassa di inizio turno come baseline. Tutte le
                    transazioni che farai saranno tracciate sotto il tuo nome.
                </div>

                <div className="p-4 bg-zinc-50 border border-zinc-200 mb-5">
                    <div className="overline mb-1">Cassa di inizio turno</div>
                    <div className="text-3xl font-black tabular-nums">
                        {baseline === null ? "…" : euro(baseline)}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                        Somma di tutti i wallet di tipo cassa/cassaforte
                    </div>
                </div>

                <label className="block mb-6">
                    <div className="overline mb-2">Note (opzionale)</div>
                    <input
                        className="inp"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Es. turno mattina"
                        data-testid="shift-open-notes"
                    />
                    <div className="text-xs text-zinc-400 mt-1">
                        Potrebbe esserti chiesta la posizione del dispositivo per verificare che tu sia in sede.
                    </div>
                </label>

                <div className="flex gap-2">
                    <button onClick={onClose} className="btn-secondary flex-1" data-testid="shift-open-cancel">
                        Annulla
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving || baseline === null}
                        className="btn-primary flex-1"
                        data-testid="shift-open-submit"
                    >
                        {saving ? "Apertura…" : "Apri turno"}
                    </button>
                </div>
                </div>
            </div>
        </div>
    ), document.body);
}


function CloseShiftModal({ shift, agencyId, onClose, onSuccess }) {
    const [step, setStep] = useState(1);
    const [posDevices, setPosDevices] = useState([]);
    const [posClosures, setPosClosures] = useState({}); // { posId: grossString }
    const [providers, setProviders] = useState([]);
    const [residui, setResidui] = useState({}); // { providerId: residuoString }
    const [syncing, setSyncing] = useState(false);
    const [syncedIds, setSyncedIds] = useState(new Set());
    const [cashCounted, setCashCounted] = useState("");
    const [cashExpected, setCashExpected] = useState(null);
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const reloadCashExpected = async () => {
        const wRes = await api.get(`/wallets?agency_id=${agencyId}`);
        const total = wRes.data
            .filter((w) => ["cassa", "cassaforte"].includes(w.type))
            .reduce((s, w) => s + (w.balance || 0), 0);
        setCashExpected(total);
    };

    useEffect(() => {
        Promise.all([
            api.get(`/pos-devices?agency_id=${agencyId}`),
            api.get(`/wallets?agency_id=${agencyId}`),
            api.get(`/providers?agency_id=${agencyId}`),
        ]).then(([pdRes, wRes, prRes]) => {
            setPosDevices(pdRes.data);
            const total = wRes.data
                .filter((w) => ["cassa", "cassaforte"].includes(w.type))
                .reduce((s, w) => s + (w.balance || 0), 0);
            setCashExpected(total);
            setProviders(prRes.data);
        });
    }, [agencyId]);

    const syncAllResidui = async () => {
        // Esegue un sync per ogni provider con un residuo inserito.
        // Salta i fornitori già sync-ati in questa sessione (idempotenza UX).
        setSyncing(true);
        const toSync = providers.filter(
            (p) => residui[p.id] !== undefined && residui[p.id] !== "" && !syncedIds.has(p.id),
        );
        let okCount = 0;
        let errCount = 0;
        for (const p of toSync) {
            const r = parseFloat(residui[p.id]);
            if (isNaN(r) || r < 0) {
                errCount++;
                continue;
            }
            try {
                await api.post("/providers/sync-residuo", {
                    provider_id: p.id,
                    residuo: r,
                });
                setSyncedIds((prev) => new Set(prev).add(p.id));
                okCount++;
            } catch (e) {
                errCount++;
                toast.error(`${p.name}: ${formatApiError(e.response?.data?.detail)}`);
            }
        }
        // Aggiorna cassa attesa dopo il sync (le tx hanno mosso il saldo)
        await reloadCashExpected();
        setSyncing(false);
        if (okCount > 0) toast.success(`Sync completato per ${okCount} fornitor${okCount === 1 ? "e" : "i"}`);
        if (errCount === 0 && okCount > 0) {
            setStep(2);
        } else if (toSync.length === 0) {
            // Nessun residuo inserito: assume "nessun gioco fatto", avanti senza sync
            setStep(2);
        }
    };

    // Cassa attesa = saldo cassa attuale − totale POS lordo che sto chiudendo
    const totalPosGross = Object.values(posClosures).reduce(
        (s, v) => s + (parseFloat(v) || 0),
        0,
    );
    const cashExpectedAfterPos =
        cashExpected === null ? null : cashExpected - totalPosGross;
    const counted = parseFloat(cashCounted);
    const discrepancy = !isNaN(counted) && cashExpectedAfterPos !== null
        ? counted - cashExpectedAfterPos
        : null;

    const submit = async () => {
        setSaving(true);
        try {
            const payload = {
                cash_counted: parseFloat(cashCounted) || 0,
                pos_closures: posDevices
                    .map((d) => ({
                        pos_device_id: d.id,
                        gross_amount: parseFloat(posClosures[d.id]) || 0,
                    }))
                    .filter((c) => c.gross_amount > 0),
                notes,
            };
            await api.post(`/shifts/${shift.id}/close`, payload);
            onSuccess();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
            setSaving(false);
        }
    };

    return createPortal((
        <div
            className="fixed inset-0 bg-white/80 backdrop-blur-sm z-[60] overflow-y-auto"
            onClick={onClose}
            data-testid="shift-close-modal"
        >
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="w-full max-w-2xl bg-white border border-zinc-200 p-6 md:p-8 shadow-2xl"
                >
                <div className="overline mb-3 flex items-center gap-2">
                    <Power size={12} weight="bold" /> Chiusura turno · Step {step} di 4
                </div>
                <h2 className="text-3xl font-black tracking-tighter mb-6">
                    {step === 1 && "Sync residuo fornitori"}
                    {step === 2 && "Chiusure POS"}
                    {step === 3 && "Conta cassa"}
                    {step === 4 && "Conferma"}
                </h2>

                {/* STEP 1 — Sync residuo fornitori */}
                {step === 1 && (
                    <div>
                        <div className="text-sm text-zinc-600 mb-5">
                            Per ogni fornitore, leggi sul portale il <strong>residuo attuale</strong> (saldo disponibile rimanente) e inseriscilo qui sotto. Il sistema calcolerà automaticamente vendite credito e vincite pagate del turno, gonfiando o sgonfiando la cassa.
                            Lascia vuoto se non hai giocato con quel fornitore.
                        </div>
                        {providers.length === 0 && (
                            <div className="p-3 bg-zinc-50 text-xs text-zinc-500">
                                Nessun fornitore configurato per questa agenzia. Procedi.
                            </div>
                        )}
                        <div className="space-y-3 max-h-[55vh] overflow-y-auto">
                            {providers.map((p) => {
                                const synced = syncedIds.has(p.id);
                                return (
                                    <div key={p.id} className={"border p-4 " + (synced ? "border-emerald-300 bg-emerald-50" : "border-zinc-200")}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="text-sm font-bold flex items-center gap-2">
                                                {p.name}
                                                {synced && <CheckCircle size={14} weight="fill" className="text-emerald-600" />}
                                            </div>
                                        </div>
                                        <label className="block">
                                            <div className="overline mb-1">Residuo portale (€)</div>
                                            <MoneyInput
                                                className="inp tabular-nums text-right"
                                                value={residui[p.id] || ""}
                                                onChange={(v) => setResidui({ ...residui, [p.id]: v })}
                                                placeholder="0,00"
                                                data-testid={`shift-sync-residuo-${p.id}`}
                                                disabled={synced}
                                            />
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex justify-end gap-2 mt-6 flex-wrap">
                            <button onClick={onClose} className="btn-secondary" data-testid="shift-close-cancel">
                                Annulla
                            </button>
                            <button
                                onClick={() => setStep(2)}
                                className="btn-secondary"
                                data-testid="shift-skip-sync"
                            >
                                Salta
                            </button>
                            <button
                                onClick={syncAllResidui}
                                disabled={syncing}
                                className="btn-primary"
                                data-testid="shift-step-sync-do"
                            >
                                {syncing ? "Sync in corso…" : "Sync e avanti"}
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 2 — POS closures */}
                {step === 2 && (
                    <div>
                        <div className="text-sm text-zinc-600 mb-5">
                            Per ogni POS device, inserisci il <strong>lordo della giornata</strong> letto dal terminale fisico (subtotale prima di chiudere la giornata sul POS).
                            Lascia vuoto/zero se non ci sono stati incassi POS.
                        </div>
                        {posDevices.length === 0 && (
                            <div className="p-3 bg-zinc-50 text-xs text-zinc-500">
                                Nessun POS device configurato per questa agenzia. Procedi alla conta cassa.
                            </div>
                        )}
                        <div className="space-y-3">
                            {posDevices.map((d) => {
                                const gross = parseFloat(posClosures[d.id]) || 0;
                                const fee = (gross * (d.fee_percentage || 0)) / 100;
                                const net = gross - fee;
                                return (
                                    <div key={d.id} className="border border-zinc-200 p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <div>
                                                <div className="text-sm font-bold">{d.name}</div>
                                                <div className="overline">{d.fee_percentage || 0}% commissione</div>
                                            </div>
                                        </div>
                                        <label className="block">
                                            <div className="overline mb-1">Lordo turno</div>
                                            <MoneyInput
                                                className="inp tabular-nums text-right"
                                                value={posClosures[d.id] || ""}
                                                onChange={(v) => setPosClosures({ ...posClosures, [d.id]: v })}
                                                placeholder="0,00"
                                                data-testid={`shift-pos-gross-${d.id}`}
                                            />
                                        </label>
                                        {gross > 0 && (
                                            <div className="text-xs text-zinc-500 mt-2 flex flex-wrap gap-4">
                                                <span>Commissione: <strong>{euro(fee)}</strong></span>
                                                <span>Netto in banca: <strong className="text-emerald-700">{euro(net)}</strong></span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex justify-between gap-2 mt-6">
                            <button onClick={() => setStep(1)} className="btn-secondary" data-testid="shift-step-back-pos">
                                Indietro
                            </button>
                            <button
                                onClick={() => setStep(3)}
                                className="btn-primary"
                                data-testid="shift-step-3"
                            >
                                Avanti
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 3 — Cash count */}
                {step === 3 && (
                    <div>
                        <div className="text-sm text-zinc-600 mb-5">
                            Apri il cassetto e <strong>conta i contanti</strong>. Inserisci l&apos;importo.
                            Il sistema confronterà con quanto atteso dopo le chiusure POS.
                        </div>
                        <div className="p-4 bg-zinc-50 border border-zinc-200 mb-5">
                            <div className="overline mb-1">Cassa attesa dopo chiusure POS</div>
                            <div className="text-2xl font-black tabular-nums">
                                {cashExpectedAfterPos === null ? "…" : euro(cashExpectedAfterPos)}
                            </div>
                            <div className="text-xs text-zinc-500 mt-1">
                                ({cashExpected === null ? "…" : euro(cashExpected)} cassa attuale
                                {totalPosGross > 0 ? ` − ${euro(totalPosGross)} POS chiusi` : ""})
                            </div>
                        </div>
                        <label className="block mb-5">
                            <div className="overline mb-2">Cassa contata (cassetto fisico)</div>
                            <MoneyInput
                                className="inp tabular-nums text-right text-2xl"
                                value={cashCounted}
                                onChange={setCashCounted}
                                placeholder="0,00"
                                data-testid="shift-cash-counted"
                                autoFocus
                            />
                        </label>
                        {discrepancy !== null && Math.abs(discrepancy) >= 0.01 && (
                            <div
                                className={`p-4 border-2 mb-5 flex items-start gap-3 ${
                                    discrepancy < 0
                                        ? "bg-red-50 border-red-300"
                                        : "bg-emerald-50 border-emerald-300"
                                }`}
                            >
                                <Warning
                                    size={20}
                                    weight="fill"
                                    className={discrepancy < 0 ? "text-red-600" : "text-emerald-600"}
                                />
                                <div>
                                    <div className="text-sm font-black">
                                        {discrepancy < 0
                                            ? `Ammanco di cassa: ${euro(Math.abs(discrepancy))}`
                                            : `Eccedenza di cassa: ${euro(discrepancy)}`}
                                    </div>
                                    <div className="text-xs mt-1 leading-relaxed">
                                        Sarà registrato come {discrepancy < 0 ? "uscita" : "entrata"} con categoria{" "}
                                        <strong>{discrepancy < 0 ? "Ammanco di cassa" : "Eccedenza di cassa"}</strong>{" "}
                                        per non rompere la quadratura.
                                    </div>
                                </div>
                            </div>
                        )}
                        {discrepancy !== null && Math.abs(discrepancy) < 0.01 && (
                            <div className="p-4 border-2 border-emerald-300 bg-emerald-50 mb-5 flex items-start gap-3">
                                <CheckCircle size={20} weight="fill" className="text-emerald-600" />
                                <div className="text-sm font-bold text-emerald-900">Cassa quadrata perfettamente</div>
                            </div>
                        )}
                        <div className="flex justify-between gap-2 mt-6">
                            <button onClick={() => setStep(2)} className="btn-secondary" data-testid="shift-step-back-cash">
                                Indietro
                            </button>
                            <button
                                onClick={() => setStep(4)}
                                disabled={cashCounted === "" || cashCounted === null}
                                className="btn-primary"
                                data-testid="shift-step-4"
                            >
                                Avanti
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 4 — Confirm */}
                {step === 4 && (
                    <div>
                        <div className="text-sm text-zinc-600 mb-5">
                            Riepilogo. Dopo la conferma verrai <strong>disconnesso automaticamente</strong>{" "}
                            così l&apos;operatore successivo può accedere con i numeri aggiornati.
                        </div>
                        <div className="space-y-2 text-sm mb-6">
                            {posDevices.map((d) => {
                                const gross = parseFloat(posClosures[d.id]) || 0;
                                if (gross === 0) return null;
                                return (
                                    <div key={d.id} className="flex justify-between border-b border-zinc-100 pb-1">
                                        <span>POS {d.name}</span>
                                        <span className="font-bold tabular-nums">{euro(gross)} lordo</span>
                                    </div>
                                );
                            })}
                            <div className="flex justify-between border-b border-zinc-100 pb-1">
                                <span>Cassa contata</span>
                                <span className="font-bold tabular-nums">{euro(parseFloat(cashCounted) || 0)}</span>
                            </div>
                            {discrepancy !== null && Math.abs(discrepancy) >= 0.01 && (
                                <div className="flex justify-between text-red-700">
                                    <span>{discrepancy < 0 ? "Ammanco" : "Eccedenza"}</span>
                                    <span className="font-bold tabular-nums">{euro(Math.abs(discrepancy))}</span>
                                </div>
                            )}
                        </div>
                        <label className="block mb-6">
                            <div className="overline mb-2">Note di chiusura (opzionale)</div>
                            <textarea
                                className="inp"
                                rows="2"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Es. consegnato a Mario"
                                data-testid="shift-close-notes"
                            />
                        </label>
                        <div className="flex justify-between gap-2">
                            <button onClick={() => setStep(3)} className="btn-secondary" data-testid="shift-step-back-confirm">
                                Indietro
                            </button>
                            <button
                                onClick={submit}
                                disabled={saving}
                                className="btn-primary"
                                data-testid="shift-close-submit"
                            >
                                {saving ? "Chiusura…" : "Chiudi turno e logout"}
                            </button>
                        </div>
                    </div>
                )}
                </div>
            </div>
        </div>
    ), document.body);
}
